import { alpha } from '@mui/material/styles';

// ----------------------------------------------------------------------

// SETUP COLORS

const GREY = {
  0: '#FFFFFF',
  100: '#F9FAFB',
  200: '#F4F6F8',
  300: '#DFE3E8',
  400: '#C4CDD5',
  500: '#919EAB',
  600: '#637381',
  700: '#454F5B',
  800: '#212B36',
  900: '#161C24',
};

const PRIMARY = {
  lighter: '#C8FAD6',
  light: '#5BE49B',
  main: '#00A76F',
  dark: '#007867',
  darker: '#004B50',
  contrastText: '#FFFFFF',
};

const SECONDARY = {
  lighter: '#EFD6FF',
  light: '#C684FF',
  main: '#8E33FF',
  dark: '#5119B7',
  darker: '#27097A',
  contrastText: '#FFFFFF',
};

const INFO = {
  lighter: '#CAFDF5',
  light: '#61F3F3',
  main: '#00B8D9',
  dark: '#006C9C',
  darker: '#003768',
  contrastText: '#FFFFFF',
};

const SUCCESS = {
  lighter: '#D3FCD2',
  light: '#77ED8B',
  main: '#22C55E',
  dark: '#118D57',
  darker: '#065E49',
  contrastText: '#ffffff',
};

const WARNING = {
  lighter: '#FFF5CC',
  light: '#FFD666',
  main: '#FFAB00',
  dark: '#B76E00',
  darker: '#7A4100',
  contrastText: GREY[800],
};

const ERROR = {
  lighter: '#FFE9D5',
  light: '#FFAC82',
  main: '#FF5630',
  dark: '#B71D18',
  darker: '#7A0916',
  contrastText: '#FFFFFF',
};

const COMMON = {
  common: {
    black: '#000000',
    white: '#FFFFFF',
  },
  primary: PRIMARY,
  secondary: SECONDARY,
  info: INFO,
  success: SUCCESS,
  warning: WARNING,
  error: ERROR,
  grey: GREY,
  divider: alpha(GREY[500], 0.2),
  action: {
    hover: alpha(GREY[500], 0.08),
    selected: alpha(GREY[500], 0.16),
    disabled: alpha(GREY[500], 0.8),
    disabledBackground: alpha(GREY[500], 0.24),
    focus: alpha(GREY[500], 0.24),
    hoverOpacity: 0.08,
    disabledOpacity: 0.48,
  },
};

export function palette(mode) {
  const light = {
    ...COMMON,
    mode: 'light',

    // Softer text contrast (less harsh than 800/600 on pure white)
    text: {
      primary: GREY[900],
      secondary: GREY[700],
      disabled: GREY[500],
    },

    background: {
      default: '#F7F6F2',
      paper: '#FFFFFF',
      neutral: '#F1EFE8',
    },
    divider: alpha(GREY[500], 0.2),

    action: {
      ...COMMON.action,
      active: GREY[700],

      // nicer hover/focus on light backgrounds
      hover: alpha(GREY[500], 0.08),
      selected: alpha(GREY[500], 0.12),
      disabled: alpha(GREY[500], 0.32),
      disabledBackground: alpha(GREY[500], 0.16),
      focus: alpha(GREY[500], 0.12),
    },
  };
  const dark = {
    ...COMMON,
    mode: 'dark',
    text: {
      primary: '#FFFFFF',
      secondary: GREY[400], // was GREY[500] — lighter, far more legible
      disabled: GREY[600],
    },
    background: {
      paper: GREY[800],
      default: GREY[900],
      neutral: alpha(GREY[500], 0.12),
    },
    divider: alpha(GREY[500], 0.32), // add this — was inheriting 0.2
    action: {
      ...COMMON.action,
      active: GREY[400], // was GREY[500]
      hover: alpha(GREY[500], 0.12),
      selected: alpha(GREY[500], 0.2),
      disabled: alpha(GREY[500], 0.4),
      disabledBackground: alpha(GREY[500], 0.2),
      focus: alpha(GREY[500], 0.2),
    },
  };

  return mode === 'light' ? light : dark;
}

// ----------------------------------------------------------------------
// Design tokens (dark-only, from the new design)
// ----------------------------------------------------------------------
const UI = {
  bg: '#0A0B08',
  bgSoft: '#0F110C',
  panel: '#13150F',
  card: 'rgba(255,255,255,0.025)',
  border: 'rgba(255,255,255,0.08)',
  borderStrong: 'rgba(255,255,255,0.16)',
  text: '#F4F6EF',
  textMuted: '#9BA394',
  textFaint: '#6E7565',
  danger: '#F2777A',
  yellow: '#E6E63C',
  green: '#7FD426',
  gradient: 'linear-gradient(90deg, #E9E637 0%, #A6DC2E 48%, #6FCF2C 100%)',
  gradientText: 'linear-gradient(90deg, #E9E637 0%, #8AD62E 100%)',
  mono: '"IBM Plex Mono","SFMono-Regular",ui-monospace,Menlo,Consolas,monospace',
};

const EMPTY = {};
const FEE_RATE = 0.01;
const MIN_FEE_USD = 2;

const CRYPTO_ASSETS = [
  { key: 'usdt-trc20', label: 'USDT', network: 'TRC-20', coingeckoId: 'tether' },
  { key: 'usdt-bep20', label: 'USDT', network: 'BEP-20', coingeckoId: 'tether' },
  { key: 'usdt-erc20', label: 'USDT', network: 'ERC-20', coingeckoId: 'tether' },
  { key: 'btc', label: 'BTC', network: 'Bitcoin', coingeckoId: 'bitcoin' },
  { key: 'eth', label: 'ETH', network: 'Ethereum', coingeckoId: 'ethereum' },
  { key: 'sol', label: 'SOL', network: 'Solana', coingeckoId: 'solana' },
  { key: 'trx', label: 'TRX', network: 'Tron', coingeckoId: 'tron' },
];

const PAYOUT_PROVIDERS = [
  { key: 'evcplus', label: 'EVC Plus', mark: 'E' },
  { key: 'zaad', label: 'Zaad', mark: 'Z' },
  { key: 'sahal', label: 'Sahal', mark: 'S' },
];

export { UI, EMPTY, FEE_RATE, MIN_FEE_USD, CRYPTO_ASSETS, PAYOUT_PROVIDERS };
